// Head — top-down (crown from above) detail panel for pain capture
// (PAIN_TRACKING_PLAN.md Task 3.3). React-free / JSX-free (hazard H7):
// scripts/validatePainMap.ts and scripts/renderBodyMap.ts import this module
// directly from the repo root. JSX starts in PainMapCanvas.tsx.
//
// Own canvas (D-PAIN-6, "Canvas conventions"): 0 0 220 220, centerline x = 110.
// The head is drawn as if looking straight down at the crown: the FACE is at
// the top (low y), the OCCIPUT at the bottom (high y), with a small nose bump at
// the very top to orient the viewer and the shoulders blocked in below as
// context.
//
// Both this panel and regionsFace.ts feed `headache` events, not `symptom`
// events (D-PAIN-2). Every region therefore carries a `headacheLocation` — the
// nearest HEADACHE_LOCATIONS value — so Session 6 can route the saved event
// without a lookup table. `headacheLocation` is optional on PainRegion and set
// ONLY in these two files.
//
// Coverage (Task 3.3): the four scalp quadrants (front-left / front-right /
// back-left / back-right, all `tissue-skin`), the vertex at the crown, and a
// `spine-c` marker at the neck for cervicogenic / neck-base headache.

import type { HeadacheLocation, PainStructure } from "@medassist/shared";
import { PAIN_STRUCTURE_META } from "@medassist/shared";
import type { PainRegion } from "./painRegions.js";
import { ellipsePath } from "./painRegions.js";

export const TOP_DOWN_VIEWBOX = "0 0 220 220";

/** A closed rounded-rectangle outline path. */
function box(x0: number, y0: number, x1: number, y1: number, r: number): string {
  return [
    `M ${x0 + r} ${y0}`,
    `L ${x1 - r} ${y0}`,
    `Q ${x1} ${y0} ${x1} ${y0 + r}`,
    `L ${x1} ${y1 - r}`,
    `Q ${x1} ${y1} ${x1 - r} ${y1}`,
    `L ${x0 + r} ${y1}`,
    `Q ${x0} ${y1} ${x0} ${y1 - r}`,
    `L ${x0} ${y0 + r}`,
    `Q ${x0} ${y0} ${x0 + r} ${y0}`,
    "Z",
  ].join(" ");
}

export const TOP_DOWN_OUTLINE: string[] = [
  ellipsePath(110, 98, 58, 70), // cranium seen from above (longer front-to-back)
  box(103, 12, 117, 32, 4), // nose bump (front, to orient the viewer)
  box(94, 150, 126, 196, 8), // neck
  ellipsePath(52, 190, 44, 24), // left shoulder (context)
  ellipsePath(168, 190, 44, 24), // right shoulder (context)
];

/** One region. `kind` is read from PAIN_STRUCTURE_META so it can never disagree
 *  with the taxonomy (validator rule S7); `side` is taken from the id suffix. */
function reg(
  id: string,
  structure: PainStructure,
  headacheLocation: HeadacheLocation,
  d: string,
): PainRegion {
  return {
    id,
    structure,
    kind: PAIN_STRUCTURE_META[structure].kind,
    side: id.endsWith("-l") ? "left" : id.endsWith("-r") ? "right" : "center",
    headacheLocation,
    d,
  };
}

export const TOP_DOWN_STRUCTURES: PainRegion[] = [
  // ── scalp quadrants (front = top, back = bottom) ──────────────────────────
  reg("tissue-skin-front-l", "tissue-skin", "frontal", ellipsePath(88, 64, 22, 26)),
  reg("tissue-skin-front-r", "tissue-skin", "frontal", ellipsePath(132, 64, 22, 26)),
  reg("tissue-skin-back-l", "tissue-skin", "occipital", ellipsePath(88, 132, 22, 26)),
  reg("tissue-skin-back-r", "tissue-skin", "occipital", ellipsePath(132, 132, 22, 26)),

  // ── vertex (crown of the head) ───────────────────────────────────────────
  reg("tissue-skin-vertex-c", "tissue-skin", "vertex", ellipsePath(110, 98, 16, 18)),

  // ── neck / cervical reference ───────────────────────────────────────────
  reg("spine-c-c", "spine-c", "neck-base", ellipsePath(110, 172, 15, 12)),
];
